import React from 'react'
import Image from 'next/legacy/image'
import styled from 'styled-components'
import { theme } from 'infra/theme'
import { FiCheck, FiChevronDown, FiChevronUp, FiCircle } from 'react-icons/fi'
import { Column, Row } from 'components/common/layout'

interface IconProps {
  size?: number
  color?: string
  strokeWidth?: number
  isOpen?: boolean
}

export const TooltipIcon: React.FC<
  IconProps & { variant?: 'info' | 'help' }
> = ({ size = 20, variant = 'help' }) => (
  <Image
    width={size}
    height={size}
    layout='fixed'
    alt={variant}
    src={`/${variant}-circle.svg`}
    priority
  />
)

export const MessagesIcon: React.FC<IconProps> = ({ size = 24 }) => (
  <svg
    width={size}
    height={size}
    viewBox='0 0 14 13'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M6.91364 8.55102L6.61683 8.20147L3.65055 10.7202C3.32576 10.9959 2.82692 10.7651 2.82692 10.339V10.2427V8.60126C2.82692 8.29591 2.57939 8.04838 2.27404 8.04838H2.22115H2.11538H1.90385H0.951923C0.702333 8.04838 0.5 7.84605 0.5 7.59646V1.5C0.5 1.22386 0.723857 1 1 1H5.92305H5.92308L10 1.00018C10.2762 1.00019 10.5 1.22405 10.5 1.50018V4.52441V7.54854C10.5 7.8247 10.2761 8.04856 9.99997 8.04854L7.40388 8.04838H7.40385H7.19231H7.08654H7.03365C6.88096 8.04838 6.73323 8.10264 6.61683 8.20147L6.91364 8.55102ZM6.91364 8.55102L6.92647 8.56613L6.91364 8.55102Z'
      fill='#898282'
      stroke='#898282'
    />
    <path
      d='M9 10.243L10.3583 12.1179C10.673 12.5522 11.3596 12.3296 11.3596 11.7933V11.7933V10.303C11.3596 10.1038 11.5211 9.94236 11.7202 9.94236V9.94236H12.4155V9.94236C12.738 9.94236 12.9995 9.68093 12.9995 9.35843V6.24724L12.9999 3.79513C13 3.63214 12.8679 3.5 12.7049 3.5V3.5H12.0786'
      stroke='#CECECE'
      strokeLinecap='round'
    />
  </svg>
)

export const EditIcon: React.FC<IconProps> = ({
  size = 24,
  color = theme.color.black1,
  strokeWidth = 1.5,
}) => (
  <svg
    width={size}
    height={size}
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <g clipPath='url(#clip0_19532_2613)'>
      <path
        d='M4 20H8L18.5 9.5C18.7626 9.23735 18.971 8.92555 19.1131 8.58239C19.2553 8.23923 19.3284 7.87143 19.3284 7.5C19.3284 7.12856 19.2553 6.76077 19.1131 6.4176C18.971 6.07444 18.7626 5.76264 18.5 5.5C18.2374 5.23735 17.9256 5.02901 17.5824 4.88687C17.2392 4.74473 16.8714 4.67157 16.5 4.67157C16.1286 4.67157 15.7608 4.74473 15.4176 4.88687C15.0744 5.02901 14.7626 5.23735 14.5 5.5L4 16V20Z'
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M13.5 6.5L17.5 10.5'
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </g>
    <defs>
      <clipPath id='clip0_19532_2613'>
        <rect width='24' height='24' fill='white' />
      </clipPath>
    </defs>
  </svg>
)

export const ChatIcon: React.FC<IconProps> = ({
  size = 24,
  color = theme.color.black1,
  strokeWidth = 1,
}) => (
  <svg
    width={size}
    height={size}
    viewBox='0 0 21 20'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M18.2917 2.5H2.04169L1.41669 3.125V14.375L2.04169 15H5.16669V18.125L6.23419 18.5675L9.80044 15H18.2917L18.9167 14.375V3.125L18.2917 2.5ZM17.6667 13.75H9.54169L9.09919 13.9325L6.41669 16.6163V14.375L5.79169 13.75H2.66669V3.75H17.6667V13.75Z'
      fill={color}
      stroke={color}
      strokeWidth={strokeWidth}
    />
    <path
      d='M5.79169 6.25H15.7917L14.5417 7.5H12.9792H10.1667H4.54169L5.16669 6.875L5.79169 6.25Z'
      fill={color}
    />
    <path
      d='M5.79169 8.75H15.7917L14.5417 10H12.9792H10.1667H4.54169L5.16669 9.375L5.79169 8.75Z'
      fill={color}
    />
  </svg>
)

export const NewspaperIcon: React.FC<IconProps> = ({
  size = 24,
  color = theme.color.black1,
}) => (
  <svg
    width={size}
    height={size}
    viewBox='0 0 16 16'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <g clipPath='url(#clip0_44112_1346)'>
      <path
        d='M13.641 2.29828C13.4707 1.49461 12.7547 0.96061 11.9217 1.00228C10.915 1.05261 9.33866 1.16961 7.11832 1.43794C4.89832 1.70628 3.33999 1.96794 2.35066 2.15861C1.53199 2.31628 0.968991 3.00494 1.00132 3.82528C1.03666 4.72194 1.13099 6.07761 1.36432 7.94828C1.46999 8.79428 1.57732 9.53161 1.68032 10.1666'
        stroke={color}
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M1.81335 13.3206C1.88635 14.1693 2.54868 14.803 3.39835 14.8603C4.42668 14.9296 6.04235 15.0013 8.33268 15.0013C10.6227 15.0013 12.2387 14.9296 13.267 14.8603C14.117 14.803 14.779 14.1693 14.852 13.3206C14.9273 12.4426 14.9993 11.1333 14.9993 9.33464C14.9993 7.53597 14.9273 6.22697 14.852 5.34864C14.779 4.49997 14.1167 3.8663 13.267 3.80897C12.2387 3.73964 10.623 3.66797 8.33268 3.66797C6.04268 3.66797 4.42668 3.73964 3.39835 3.80897C2.54835 3.8663 1.88635 4.49997 1.81335 5.34864C1.73802 6.22664 1.66602 7.53597 1.66602 9.33464C1.66602 11.1333 1.73802 12.4426 1.81335 13.3206Z'
        stroke={color}
        strokeLinejoin='round'
      />
      <path
        d='M6.84312 6.33203H3.66602M7 9.5H3.66602M12.9993 12.332H3.66602'
        stroke={color}
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M9.02491 9.09225C9.04948 9.57995 9.42004 9.95051 9.90774 9.97508C10.1909 9.98937 10.5526 9.99994 11 9.99994C11.3642 10.0008 11.7284 9.99253 12.0922 9.97508C12.5797 9.95051 12.9505 9.57995 12.9751 9.09225C12.9894 8.80912 12.9999 8.44741 12.9999 8C13.0008 7.63586 12.9925 7.27176 12.9751 6.90803C12.9505 6.42033 12.58 6.04949 12.0922 6.0252C11.7284 6.00766 11.3642 5.99928 11 6.00006C10.6359 5.99919 10.2717 6.00748 9.90802 6.02492C9.42032 6.04949 9.04948 6.42005 9.02519 6.90775C9.00765 7.27156 8.99927 7.63576 9.00005 8C9.00005 8.44741 9.01091 8.80912 9.02491 9.09225Z'
        stroke={color}
        strokeLinejoin='round'
      />
    </g>
    <defs>
      <clipPath id='clip0_44112_1346'>
        <rect width={size} height={size} fill='white' />
      </clipPath>
    </defs>
  </svg>
)

export const HandShakeIcon: React.FC<IconProps> = ({
  size = 24,
  color = theme.color.primary,
}) => (
  <svg
    width={size}
    height={size - 5}
    viewBox='0 0 21 16'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M6.61687 3.44587C6.37458 3.44308 6.18799 3.50157 6.00419 3.54891C5.6143 3.64917 5.23833 3.68538 4.84565 3.52107C4.42234 3.34562 3.97119 3.2342 3.53395 3.0866C3.30837 3.01141 3.15242 2.80532 3.14128 2.59088C3.13014 2.3458 3.25546 2.14529 3.48382 2.03947C3.59522 1.98934 3.70105 1.98378 3.81801 2.02277C4.30259 2.18708 4.78717 2.34583 5.27175 2.51014C5.36365 2.54077 5.45277 2.5519 5.54746 2.5324C6.75055 2.27062 7.95085 1.9949 9.15952 1.75818C9.67194 1.65793 10.0618 1.35994 10.474 1.09537C10.9948 0.763962 11.5323 0.502175 12.1672 0.535595C12.7354 0.566229 13.2701 0.713833 13.7574 1.02575C14.4676 1.47969 15.2 1.89744 15.9631 2.2567C16.4644 2.49342 16.9907 2.45999 17.5115 2.35695C17.971 2.26505 18.4194 2.13138 18.8734 2.01441C19.2103 1.92808 19.5028 2.06732 19.5891 2.35416C19.6921 2.69393 19.5529 2.96687 19.1908 3.06991C18.4222 3.28714 17.6536 3.52385 16.8487 3.54891C16.3391 3.56562 15.8461 3.44586 15.3866 3.22306C14.5957 2.83874 13.841 2.38758 13.0891 1.93642C12.4736 1.56603 11.8386 1.50753 11.1981 1.92249C10.3264 2.48783 9.42408 2.99471 8.48835 3.44866C8.17086 3.60183 7.86174 3.77449 7.5916 4.00564C7.40501 4.16717 7.24069 4.35375 7.33817 4.6211C7.43843 4.90517 7.61388 5.12797 7.95086 5.15582C8.35189 5.18646 8.72228 5.06112 9.08711 4.93301C10.0034 4.60996 10.9196 4.2869 11.8525 4.01397C12.3483 3.86916 12.5711 3.98336 12.7409 4.49022C12.9777 5.20316 13.4817 5.70164 14.0833 6.10546C14.8074 6.59004 15.5509 7.04678 16.2862 7.51465C16.6816 7.7653 17.0966 7.98808 17.467 8.27215C17.7928 8.52279 18.0964 8.53114 18.464 8.40582C18.9068 8.25543 19.3635 8.14961 19.8147 8.02986C20.0737 7.96302 20.3939 8.14681 20.4747 8.39745C20.5638 8.67595 20.4106 9.00179 20.1238 9.09926C19.9344 9.1661 19.7367 9.21625 19.5417 9.26081C19.3941 9.29701 19.344 9.34991 19.3941 9.517C19.6587 10.4026 19.28 11.5639 18.23 11.9705C17.6981 12.1766 17.169 12.1014 16.6454 11.9176C16.0244 11.7004 15.5119 11.2966 14.9717 10.9429C14.2225 10.4555 13.4817 9.95704 12.7354 9.4641C12.4708 9.28865 12.3928 8.99345 12.5488 8.73445C12.6992 8.47824 13.0389 8.37797 13.3007 8.54506C13.7574 8.83191 14.203 9.13827 14.6542 9.43626C15.2362 9.82337 15.8127 10.2188 16.3976 10.6003C16.7067 10.8009 17.0409 10.9596 17.4141 10.9986C18.0434 11.0654 18.5531 10.3804 18.3164 9.78717C18.2774 9.68969 18.2161 9.62841 18.0991 9.67019C17.8234 9.77044 17.5951 9.65625 17.3695 9.51421C16.1051 8.71215 14.838 7.91566 13.5764 7.11082C12.8802 6.66523 12.2981 6.1027 11.9082 5.36469C11.8358 5.23102 11.7579 5.20873 11.6325 5.2505C10.9781 5.47329 10.3236 5.69053 9.67194 5.91889C9.12609 6.11105 8.57746 6.27812 7.99263 6.27534C6.95385 6.27255 6.08773 5.29226 6.22141 4.25626C6.25762 3.96941 6.42471 3.72715 6.61687 3.44587ZM8.6081 10.7368C8.45493 10.7814 8.41594 10.7424 8.35745 10.6143C8.11516 10.0824 7.72249 9.73703 7.12651 9.64234C6.79511 9.58943 6.48041 9.6312 6.17128 9.73981C6.06267 9.7788 6.00419 9.76487 5.94849 9.66182C5.45834 8.77622 4.45855 8.67318 3.73447 9.01016C3.48382 9.12713 3.33622 9.08812 3.13849 8.95723C2.55087 8.56734 1.9549 8.18859 1.35892 7.81262C1.13334 7.67059 0.854849 7.69566 0.671044 7.84883C0.506733 7.98808 0.453817 8.23596 0.542935 8.46154C0.595849 8.59243 0.693323 8.68432 0.81029 8.7623C1.42855 9.15776 2.04123 9.56158 2.66505 9.95426C2.79316 10.035 2.85164 10.1158 2.81265 10.269C2.73189 10.5892 2.75139 10.9123 2.78481 11.2381C2.89063 12.3326 3.83751 13.0316 4.90971 12.7977C5.12972 12.7503 5.12972 12.8784 5.16035 13.0121C5.38036 13.9896 6.44699 14.5939 7.38551 14.2486C7.59995 14.1706 7.68629 14.2208 7.81161 14.3906C8.4967 15.3264 9.96993 15.2958 10.5993 14.3377C10.6884 14.2041 10.7497 14.2096 10.8695 14.2876C11.3875 14.6246 11.8999 14.9755 12.4402 15.2818C13.1754 15.6996 14.2309 15.3793 14.6458 14.6469C14.7294 14.502 14.7628 14.3544 14.696 14.1929C14.5177 13.7557 14.0499 13.6861 13.724 14.0509C13.3954 14.4185 13.1308 14.4547 12.7214 14.1873C12.2285 13.8643 11.7439 13.5218 11.2371 13.2238C10.9474 13.0539 10.7776 12.8645 10.8611 12.5832C10.9252 11.3551 10.0061 10.3386 8.6081 10.7368ZM7.44678 12.6473C7.43007 13.0065 7.26576 13.2015 6.96499 13.2293C6.62523 13.2628 6.34395 13.0789 6.26597 12.7559C6.18521 12.43 6.23534 12.0959 6.23255 11.7673C6.22977 11.5612 6.19635 11.3523 6.27154 11.1462C6.38015 10.8427 6.57788 10.7034 6.89258 10.7229C7.20449 10.7424 7.41058 10.9262 7.45792 11.2437C7.47742 11.3718 7.47463 11.5027 7.47741 11.6308C7.4802 11.7617 7.47741 11.8898 7.47741 12.0207C7.46906 12.0207 7.46071 12.0207 7.45235 12.0207C7.44678 12.2323 7.45513 12.4412 7.44678 12.6473ZM9.81397 13.3352C9.8084 13.6916 9.63017 13.9255 9.34611 13.9673C8.99242 14.0174 8.6805 13.8309 8.61367 13.4827C8.5329 13.0594 8.51898 12.6194 8.61645 12.1961C8.7 11.8285 8.93115 11.6948 9.30433 11.7366C9.5884 11.7673 9.78612 11.9845 9.81119 12.3187C9.82511 12.5053 9.81397 12.6918 9.81397 12.8812H9.81676C9.81397 13.0344 9.81676 13.1848 9.81397 13.3352ZM5.06288 11.3049C4.98769 11.589 4.81781 11.7394 4.55602 11.7505C4.26639 11.7617 4.01296 11.6085 3.93498 11.3439C3.83751 11.007 3.82915 10.6588 3.9322 10.3219C4.01574 10.0434 4.25803 9.91805 4.60615 9.95147C4.86793 9.97653 5.06009 10.1631 5.09351 10.4444C5.10744 10.5642 5.0963 10.6895 5.0963 10.812C5.10187 10.9763 5.10465 11.1434 5.06288 11.3049ZM16.6315 12.8923C16.7178 12.742 16.7067 12.6083 16.6677 12.4579C16.612 12.2463 16.5062 12.0931 16.2806 12.0513C16.055 12.0095 15.8796 12.1098 15.7487 12.3019C15.5231 12.6389 15.2418 12.6807 14.8853 12.4858C14.1362 12.0819 13.4093 11.6224 12.6936 11.1601C12.3538 10.9429 12.0475 10.6728 11.6994 10.475C11.4125 10.3107 11.1117 10.3386 10.9196 10.6226C10.7302 10.9067 10.8806 11.1601 11.1006 11.3774C11.1229 11.3996 11.1591 11.4247 11.1897 11.4442C12.2731 12.1738 13.3731 12.8757 14.5316 13.4716C14.8519 13.6359 15.1778 13.7557 15.5287 13.7056C16.0801 13.6304 16.3697 13.3407 16.6315 12.8923Z'
      fill={color}
    />
  </svg>
)

export const DropdownArrowIcon: React.FC<IconProps> = ({
  size = 24,
  color = theme.color.black1,
  strokeWidth = 2,
  isOpen,
}) => {
  return isOpen ? (
    <FiChevronUp size={size} color={color} strokeWidth={strokeWidth} />
  ) : (
    <FiChevronDown size={size} color={color} strokeWidth={strokeWidth} />
  )
}

export const InfoCircleIcon: React.FC<IconProps> = ({
  size = 24,
  color = theme.color.secondary,
}) => (
  <svg
    width={size}
    height={size}
    viewBox='0 0 17 18'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M9.66667 4.88883C9.66667 5.46661 9.23333 5.97217 8.58333 5.97217C7.93333 5.97217 7.5 5.46661 7.5 4.88883C7.5 4.31106 8.00556 3.8055 8.58333 3.8055C9.16111 3.8055 9.66667 4.31106 9.66667 4.88883ZM7.36111 12.9722C6.94444 12.9722 6.66667 12.71 6.66667 12.3167C6.66667 11.9235 6.94444 11.6613 7.36111 11.6613H7.91667C7.98611 11.6613 8.05556 11.5958 8.05556 11.5302V8.58078C8.05556 8.51524 7.98611 8.4497 7.91667 8.4497H7.36111C6.94444 8.4497 6.66667 8.18752 6.66667 7.79426C6.66667 7.40101 6.94444 7.13883 7.36111 7.13883H8.05556C8.81944 7.13883 9.44444 7.72872 9.44444 8.4497V11.5958C9.44444 11.6613 9.51389 11.7268 9.58333 11.7268H10.1389C10.5556 11.7268 10.8333 11.989 10.8333 12.3823C10.8333 12.7755 10.5556 12.9722 10.1389 12.9722H7.36111ZM8.33333 0.472168C3.75 0.472168 0 4.22217 0 8.8055C0 13.3888 3.75 17.1388 8.33333 17.1388C12.9167 17.1388 16.6667 13.3888 16.6667 8.8055C16.6667 4.22217 12.9167 0.472168 8.33333 0.472168Z'
      fill={color}
    />
  </svg>
)

export const CheckCircleIcon = ({ size = 30 }: { size?: number }) => (
  <CheckIconWrapper size={size}>
    <Image
      src='/check-white1.svg'
      width={size}
      height={size}
      alt='check icon'
      priority
    />
  </CheckIconWrapper>
)

export const CheckIcon: React.FC<IconProps> = ({
  size = 44,
  strokeWidth = 1.5,
  color = theme.color.primary,
}) => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    className='icon icon-tabler icon-tabler-check'
    width={size}
    height={size}
    viewBox='0 0 24 24'
    strokeWidth={strokeWidth}
    stroke={color}
    fill='none'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <path stroke='none' d='M0 0h24v24H0z' fill='none' />
    <path d='M5 12l5 5l10 -10' />
  </svg>
)

const CheckIconWrapper = styled(Row)<{ size: number }>`
  flex: none;
  padding: ${(p) => (p.size >= 24 ? 8 : p.size >= 16 ? 6 : 4)}px;
  background-color: ${(p) => p.theme.color.primary};
  border-radius: 50%;
`

export const RadioIcon: React.FC<{ isSelected: boolean; size?: number }> = ({
  isSelected,
  size = 18,
}) => (
  <RadioIconWrapper isSelected={isSelected} size={size}>
    {isSelected && (
      <FiCircle
        size={size - 6}
        style={{ flex: 1 }}
        color={theme.color.primary}
        fill={theme.color.primary}
      />
    )}
  </RadioIconWrapper>
)

const RadioIconWrapper = styled(Column)<{ isSelected: boolean; size: number }>`
  flex: none;
  width: ${(p) => p.size}px;
  height: ${(p) => p.size}px;
  align-items: center;
  border-radius: 100px;
  border: 1px solid
    ${(p) => (p.isSelected ? p.theme.color.primary : p.theme.color.gray2)};
  background-color: ${(p) => p.theme.color.white1};
`

export const Checker: React.FC<{
  isChecked: boolean
  size?: number
  color?: string
}> = ({ isChecked, size = 19, color = theme.color.primary }) => (
  <Circle isChecked={isChecked} size={size} color={color}>
    <FiCheck size={size - 7} strokeWidth={4} color={theme.color.white1} />
  </Circle>
)

const Circle = styled(Row)<{
  isChecked: boolean
  size: number
  color: string
}>`
  flex: none;
  width: ${(p) => p.size}px;
  height: ${(p) => p.size}px;
  justify-content: center;
  border-radius: 100px;
  background-color: ${(p) => (p.isChecked ? p.color : p.theme.color.gray3)};
`

export const OrderByIcon: React.FC<IconProps> = ({ size = 24 }) => (
  <Image
    width={size}
    height={size}
    layout='fixed'
    alt='order by icon'
    src='/order-by.svg'
    priority
    style={{ flex: 'none' }}
  />
)

export const EarthIcon: React.FC<IconProps> = ({
  color = theme.color.black2,
  strokeWidth = 2,
  size = 24,
}) => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    viewBox={`0 0 24 24`}
    fill='none'
    stroke={color}
    strokeLinecap='round'
    strokeLinejoin='round'
    width={size}
    height={size}
    strokeWidth={strokeWidth}
  >
    <path d='M3 12a9 9 0 1 0 18 0a9 9 0 0 0 -18 0'></path>
    <path d='M3.6 9h16.8'></path>
    <path d='M3.6 15h16.8'></path>
    <path d='M11.5 3a17 17 0 0 0 0 18'></path>
    <path d='M12.5 3a17 17 0 0 1 0 18'></path>
  </svg>
)

export const SproutIcon: React.FC<IconProps> = ({
  size = 24,
  color = theme.color.black2,
}) => (
  <svg
    width={size}
    height={size}
    viewBox='0 0 18 18'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M1.5 16.5001V15.0001C1.5 15.0001 5.25 13.5001 9 13.5001C12.75 13.5001 16.5 15.0001 16.5 15.0001V16.5001H1.5ZM8.475 6.82507C7.575 3.90007 3 4.57507 3 4.57507C3 4.57507 3.15 10.4251 7.425 9.52507C7.125 7.35007 6 6.75007 6 6.75007C8.1 6.75007 8.25 9.30007 8.25 9.30007V12.7501C8.475 12.7501 8.775 12.7501 9 12.7501C9.225 12.7501 9.525 12.7501 9.75 12.7501V9.60007C9.75 9.60007 9.75 6.67507 12 5.92507C12 5.92507 10.5 8.17507 10.5 9.67507C15.75 10.2001 15.75 3.00007 15.75 3.00007C15.75 3.00007 9.075 2.25007 8.475 6.82507Z'
      fill={color}
    />
  </svg>
)

export const TwinkleIcon: React.FC<IconProps> = ({
  size = 18,
  color = '#06D6DF',
}) => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width={size}
    height={size}
    viewBox='0 0 18 19'
    fill='none'
  >
    <path
      d='M8.61501 7.5279C8.73876 7.15665 9.26301 7.15665 9.38676 7.5279L10.1124 9.70702C10.2721 10.1861 10.5412 10.6213 10.8984 10.9782C11.2555 11.3352 11.6909 11.6041 12.17 11.7635L14.348 12.4891C14.7193 12.6129 14.7193 13.1371 14.348 13.2609L12.1689 13.9865C11.6899 14.1462 11.2546 14.4153 10.8977 14.7725C10.5407 15.1296 10.2718 15.565 10.1124 16.0442L9.38676 18.2221C9.36008 18.3034 9.30841 18.3742 9.23911 18.4244C9.16982 18.4746 9.08645 18.5016 9.00089 18.5016C8.91533 18.5016 8.83195 18.4746 8.76266 18.4244C8.69336 18.3742 8.64169 18.3034 8.61501 18.2221L7.88939 16.043C7.72978 15.5641 7.46085 15.129 7.1039 14.772C6.74695 14.4151 6.31179 14.1461 5.83289 13.9865L3.65376 13.2609C3.57247 13.2342 3.50168 13.1825 3.45149 13.1133C3.4013 13.044 3.37428 12.9606 3.37428 12.875C3.37428 12.7895 3.4013 12.7061 3.45149 12.6368C3.50168 12.5675 3.57247 12.5158 3.65376 12.4891L5.83289 11.7635C6.31179 11.6039 6.74695 11.335 7.1039 10.978C7.46085 10.6211 7.72978 10.1859 7.88939 9.70702L8.61501 7.5279ZM4.26914 1.79152C4.28527 1.74281 4.31634 1.70041 4.35795 1.67036C4.39955 1.64031 4.44957 1.62414 4.50089 1.62414C4.55221 1.62414 4.60222 1.64031 4.64382 1.67036C4.68543 1.70041 4.7165 1.74281 4.73264 1.79152L5.16801 3.09877C5.36264 3.68152 5.81939 4.13827 6.40214 4.3329L7.70939 4.76827C7.7581 4.78441 7.8005 4.81548 7.83055 4.85709C7.8606 4.89869 7.87677 4.9487 7.87677 5.00002C7.87677 5.05134 7.8606 5.10136 7.83055 5.14296C7.8005 5.18457 7.7581 5.21564 7.70939 5.23177L6.40214 5.66715C6.11456 5.76259 5.85324 5.92386 5.63898 6.13812C5.42472 6.35237 5.26345 6.6137 5.16801 6.90127L4.73264 8.20852C4.7165 8.25724 4.68543 8.29964 4.64382 8.32969C4.60222 8.35974 4.55221 8.37591 4.50089 8.37591C4.44957 8.37591 4.39955 8.35974 4.35795 8.32969C4.31634 8.29964 4.28527 8.25724 4.26914 8.20852L3.83376 6.90127C3.73832 6.6137 3.57705 6.35237 3.36279 6.13812C3.14854 5.92386 2.88721 5.76259 2.59964 5.66715L1.29239 5.23177C1.24367 5.21564 1.20127 5.18457 1.17122 5.14296C1.14117 5.10136 1.125 5.05134 1.125 5.00002C1.125 4.9487 1.14117 4.89869 1.17122 4.85709C1.20127 4.81548 1.24367 4.78441 1.29239 4.76827L2.59964 4.3329C2.88721 4.23746 3.14854 4.07619 3.36279 3.86193C3.57705 3.64768 3.73832 3.38635 3.83376 3.09877L4.26914 1.79152ZM12.2218 0.611399C12.2329 0.579367 12.2537 0.55159 12.2813 0.531931C12.3089 0.512273 12.342 0.501709 12.3759 0.501709C12.4098 0.501709 12.4428 0.512273 12.4705 0.531931C12.4981 0.55159 12.5189 0.579367 12.53 0.611399L12.8203 1.48215C12.9496 1.8714 13.2545 2.17627 13.6438 2.30565L14.5145 2.5959C14.5465 2.60701 14.5743 2.62782 14.594 2.65544C14.6136 2.68306 14.6242 2.71612 14.6242 2.75002C14.6242 2.78393 14.6136 2.81699 14.594 2.84461C14.5743 2.87223 14.5465 2.89304 14.5145 2.90415L13.6438 3.1944C13.4521 3.25852 13.278 3.36629 13.1351 3.50919C12.9921 3.6521 12.8844 3.82624 12.8203 4.0179L12.53 4.88865C12.5189 4.92068 12.4981 4.94846 12.4705 4.96812C12.4428 4.98778 12.4098 4.99834 12.3759 4.99834C12.342 4.99834 12.3089 4.98778 12.2813 4.96812C12.2537 4.94846 12.2329 4.92068 12.2218 4.88865L11.9315 4.0179C11.8674 3.82624 11.7596 3.6521 11.6167 3.50919C11.4738 3.36629 11.2997 3.25852 11.108 3.1944L10.2384 2.90415C10.2064 2.89304 10.1786 2.87223 10.1589 2.84461C10.1393 2.81699 10.1287 2.78393 10.1287 2.75002C10.1287 2.71612 10.1393 2.68306 10.1589 2.65544C10.1786 2.62782 10.2064 2.60701 10.2384 2.5959L11.1091 2.30565C11.4984 2.17627 11.8033 1.8714 11.9326 1.48215L12.2218 0.611399Z'
      fill={color}
    />
  </svg>
)
